import {
	computed,
	inject,
	Injectable,
	InjectionToken,
	signal,
} from "@angular/core";
import { AuthClientService } from "./auth-client.service";
import { StorageKeys, StorageService } from "./storage.service";

/**
 * Configuration token for AuthService behavior
 */
export interface AuthConfig {
	enableAnonymousSignIn?: boolean;
}

export const AUTH_CONFIG = new InjectionToken<AuthConfig>("AUTH_CONFIG", {
	providedIn: "root",
	factory: () => ({ enableAnonymousSignIn: true }),
});

/**
 * Auth types - these would normally come from a shared types library
 * but for rapid prototyping, we define them inline
 */
export interface User {
	id: string;
	email: string;
	/** OAuth / sign-up name — legal or account-identity name. May be "Anonymous". */
	name?: string;
	/**
	 * Player-facing name shown on leaderboards and team rosters. May be null
	 * for anon users who haven't chosen one yet; callers should fall back to
	 * `name` or a placeholder. Separate from `name` so real-account users can
	 * still pick a fun game handle without changing their OAuth identity.
	 */
	displayName?: string | null;
	createdAt?: Date;
	role?: "player" | "moderator" | "admin";
	/**
	 * True when this is a Better Auth anonymous user (created via auto-provision).
	 * Typed as `boolean | null | undefined` to match what Better Auth returns
	 * from session queries — null and undefined both mean "not anonymous" in
	 * our interpretation (see `isAnonymous` computed signal).
	 */
	isAnonymous?: boolean | null;
}

export interface AuthResponse {
	token: string;
	user: User;
}

export interface AuthState {
	currentUser: User | null;
	isAuthenticated: boolean;
	isLoading: boolean;
	error: string | null;
}

/**
 * Authentication service providing signal-based state management
 * and API integration for user authentication.
 * Now integrated with Better Auth for OAuth support.
 */
@Injectable({
	providedIn: "root",
})
export class AuthService {
	private readonly authClient = inject(AuthClientService);
	private readonly storageService = inject(StorageService);
	private readonly config = inject(AUTH_CONFIG);

	// Private writable state signal
	private readonly state = signal<AuthState>({
		currentUser: null,
		isAuthenticated: false,
		isLoading: false,
		error: null,
	});

	// Public read-only selectors
	readonly currentUser = computed(() => this.state().currentUser);
	readonly isAuthenticated = computed(() => this.state().isAuthenticated);
	/**
	 * True when the signed-in user is a Better Auth anonymous user — i.e. the
	 * app silently provisioned them on first launch so they could browse/join
	 * without friction. They have a real session and a real userId; they just
	 * haven't claimed an email/provider yet.
	 *
	 * Pair this with `isAuthenticated`:
	 *  - `isAuthenticated() && !isAnonymous()` → full account
	 *  - `isAuthenticated() && isAnonymous()`  → guest
	 *  - `!isAuthenticated()`                  → init failed (server unreachable)
	 */
	readonly isAnonymous = computed(
		() => this.state().currentUser?.isAnonymous === true,
	);
	readonly isLoading = computed(() => this.state().isLoading);
	readonly error = computed(() => this.state().error);

	/**
	 * Set when app init fails to establish a connection to the server.
	 * - "network": fetch threw (server unreachable, offline, CORS/PNA block)
	 * - "server": server responded but returned a 5xx error
	 */
	readonly initError = signal<"network" | "server" | null>(null);

	/**
	 * Initialize authentication state
	 * Called by APP_INITIALIZER to ensure auth is loaded before routing
	 * @returns Promise that resolves when auth state is initialized
	 */
	async initialize(): Promise<void> {
		await this.loadStoredAuth();
	}

	/** Retry after a network init failure. Clears the error then re-runs init. */
	async retryInit(): Promise<void> {
		this.initError.set(null);
		await this.loadStoredAuth();
	}

	/**
	 * Sign in with email and password
	 */
	async signIn(email: string, password: string): Promise<void> {
		this.setLoading(true);
		this.clearError();

		try {
			const { data, error } = await this.authClient.client.signIn.email({
				email,
				password,
			});

			if (error) {
				this.setError(error.message || "Sign in failed");
				return;
			}

			if (data?.user) {
				await this.handleAuthSuccess(data.user, data.token);
			}
		} catch (error) {
			this.handleAuthError(error);
		} finally {
			this.setLoading(false);
		}
	}

	/**
	 * Sign up with email and password
	 */
	async signUp(email: string, password: string, name?: string): Promise<void> {
		this.setLoading(true);
		this.clearError();

		try {
			const { data, error } = await this.authClient.client.signUp.email({
				email,
				password,
				name: name || "",
			});

			if (error) {
				this.setError(error.message || "Sign up failed");
				return;
			}

			if (data?.user) {
				await this.handleAuthSuccess(data.user, data.token!);
			}
		} catch (error) {
			this.handleAuthError(error);
		} finally {
			this.setLoading(false);
		}
	}

	/**
	 * Sign in with Google OAuth
	 * Uses Better Auth client to handle the OAuth flow
	 * May redirect to Google login page or return user if already authenticated
	 */
	async signInWithGoogle(): Promise<void> {
		this.setLoading(true);
		this.clearError();

		try {
			const { data, error } = await this.authClient.client.signIn.social({
				provider: "google",
				callbackURL: `${window.location.origin}/auth/callback`,
			});

			if (error) {
				this.setError(error.message || "Google sign-in failed");
				this.setLoading(false);
				return;
			}

			if (!data) {
				this.setError("Google sign-in failed: no response");
				this.setLoading(false);
				return;
			}

			// Social login may return a redirect URL or user data
			if ("redirect" in data && data.redirect && "url" in data) {
				// Redirect to OAuth provider
				window.location.href = data.url as string;
			} else if ("user" in data && data.user) {
				// Already authenticated, set user
				await this.handleAuthSuccess(data.user, data.token);
				this.setLoading(false);
			}
		} catch (error) {
			console.error("Google sign-in error:", error);
			this.handleAuthError(error);
			this.setLoading(false);
		}
	}

	/**
	 * Sign in anonymously. Creates a Better Auth anonymous user (real session,
	 * real userId, `isAnonymous = true`, synthetic email at `@thedash.app`).
	 *
	 * Called automatically from `loadStoredAuth()` on first launch so every user
	 * has a session before they try to do anything. Also callable explicitly
	 * from a "continue as guest" UI, optionally passing a displayName captured
	 * at join time.
	 *
	 * Notes on the update write:
	 * - We write the player's chosen name to `displayName`, NOT `name`. `name`
	 *   stays as Better Auth's default "Anonymous" for anons; `displayName` is
	 *   what the rest of the app renders. This keeps the identity contract
	 *   consistent once they upgrade (`name` becomes their OAuth name, which
	 *   they may or may not want on the leaderboard).
	 * - updateUser for a custom column relies on the Drizzle adapter passing
	 *   it through; if the column doesn't exist the call silently no-ops.
	 */
	async signInAnonymously(displayName?: string): Promise<void> {
		this.setLoading(true);
		this.clearError();

		try {
			const { data, error } = await this.authClient.client.signIn.anonymous();

			if (error) {
				console.warn("Anonymous sign-in failed:", error.message);
				this.setError(error.message || "Anonymous sign-in failed");
				return;
			}

			if (data?.user) {
				let user = data.user as User;

				if (displayName?.trim()) {
					const trimmed = displayName.trim();
					try {
						// Cast: Better Auth's updateUser input type is the built-in
						// fields only (name, image). Our Drizzle adapter writes any
						// registered `additionalFields` through, so we send `displayName`
						// and cast the argument to the broadest "extra field" shape.
						await this.authClient.client.updateUser({
							displayName: trimmed,
						} as Record<string, unknown>);
						user = { ...user, displayName: trimmed };
					} catch (updateError) {
						console.warn("Failed to set anonymous displayName:", updateError);
					}
				}

				await this.handleAuthSuccess(user, data.token);
			}
		} catch (error) {
			console.warn("Anonymous sign-in error:", error);
			this.handleAuthError(error);
		} finally {
			this.setLoading(false);
		}
	}

	/**
	 * Update the signed-in user's displayName on the server and mirror it into
	 * local state. Used by the join flow's "pick a team name" step for anon
	 * users, but it's also the right call for any future "edit your name" UI.
	 *
	 * Fails silently on server error (we log but don't throw) because the join
	 * flow should still proceed if the name write failed — a generic fallback
	 * beats a hard error page.
	 */
	async setDisplayName(displayName: string): Promise<void> {
		const trimmed = displayName.trim();
		if (!trimmed) return;

		try {
			await this.authClient.client.updateUser({
				displayName: trimmed,
			} as unknown as Parameters<typeof this.authClient.client.updateUser>[0]);
			const current = this.state().currentUser;
			if (current) {
				const next: User = { ...current, displayName: trimmed };
				this.setUser(next);
				await this.saveUserToStorage(next);
			}
		} catch (err) {
			console.warn("Failed to update displayName:", err);
		}
	}

	/**
	 * Sign out current user
	 */
	async signOut(): Promise<void> {
		this.setLoading(true);

		try {
			const { error } = await this.authClient.client.signOut();
			if (error) {
				console.error("Sign out error:", error.message);
			}
		} catch (error) {
			// Log error but continue with local signout
			console.error("Sign out error:", error);
		} finally {
			await this.clearAuthState();
			this.setLoading(false);
		}
	}

	/**
	 * Get current session from server
	 * Validates and refreshes authentication state
	 */
	async getSession(): Promise<User | null> {
		this.setLoading(true);

		try {
			const { data, error } = await this.authClient.client.getSession();

			if (error || !data?.user) {
				await this.clearAuthState();
				return null;
			}

			this.setUser(data.user);
			await this.saveUserToStorage(data.user);
			return data.user;
		} catch (_error) {
			await this.clearAuthState();
			return null;
		} finally {
			this.setLoading(false);
		}
	}

	/**
	 * Load stored auth state asynchronously
	 * Called by APP_INITIALIZER to initialize auth state before routing
	 * Better Auth uses cookies for session management - we validate with backend
	 * If no session and anonymous sign-in is enabled, creates anonymous user for quick access
	 */
	private async loadStoredAuth(): Promise<void> {
		let data: Awaited<
			ReturnType<typeof this.authClient.client.getSession>
		>["data"];
		let error: Awaited<
			ReturnType<typeof this.authClient.client.getSession>
		>["error"];

		const maxAttempts = 3;
		let lastFetchError: unknown;
		for (let attempt = 0; attempt < maxAttempts; attempt++) {
			try {
				({ data, error } = await this.authClient.client.getSession());
				lastFetchError = undefined;
				break;
			} catch (fetchError) {
				lastFetchError = fetchError;
				if (attempt < maxAttempts - 1) {
					await new Promise((r) => setTimeout(r, 500 * 2 ** attempt));
				}
			}
		}
		if (lastFetchError) {
			console.warn(
				"Auth init: server unreachable after retries —",
				lastFetchError,
			);
			this.initError.set("network");
			return;
		}

		if (error) {
			const status = (error as { status?: number }).status ?? 0;
			if (status >= 500) {
				// Server is reachable but has an internal error
				console.warn("Auth init: server error —", error);
				this.initError.set("server");
				return;
			}
			// 4xx or other client-side errors — treat as unauthenticated (not a crash)
			console.warn("Auth init: auth error (unauthenticated) —", error);
			return;
		}

		if (data?.user) {
			this.setUser(data.user);
			await this.saveUserToStorage(data.user);
			await this.storageService.set(StorageKeys.AuthToken, data.session.token);
			return;
		}

		// Server reachable, no session. If anonymous sign-in is enabled we silently
		// provision an anon user so the rest of the app can assume `isAuthenticated`
		// is always true after init. The anon user gets a real Better Auth session
		// and userId; their FKs follow them on upgrade via server-side onLinkAccount.
		//
		// If anon provisioning fails (e.g. server is flaky), we leave the user
		// unauthenticated rather than block render — pages that need a user can
		// either gate their own content or nudge the user to retry.
		//
		// TODO(phase-4): scheduled cleanup cron that purges anon users with zero
		// meaningful activity after N days so this doesn't bloat the DB over time.
		if (this.config.enableAnonymousSignIn) {
			await this.signInAnonymously();
		}
	}

	/**
	 * Handle successful authentication
	 * We sync to IndexedDB for API client usage
	 */
	private async handleAuthSuccess(user: User, token: string): Promise<void> {
		// Better Auth client automatically stores token in localStorage
		// We need to sync it to our IndexedDB storage for API client
		await this.storageService.set(StorageKeys.AuthToken, token);

		await this.saveUserToStorage(user);
		this.setUser(user);
	}

	/**
	 * Handle Better Auth client errors
	 */
	private handleAuthError(error: unknown): void {
		if (error instanceof Error) {
			// Better Auth client errors have a message property
			const message = error.message || "An error occurred. Please try again.";
			this.setError(message);
		} else {
			this.setError("An unexpected error occurred.");
		}
	}

	/**
	 * State mutation methods
	 */
	private setUser(user: User): void {
		this.state.update((state) => ({
			...state,
			currentUser: user,
			isAuthenticated: true,
			error: null,
		}));
	}

	private setLoading(isLoading: boolean): void {
		this.state.update((state) => ({ ...state, isLoading }));
	}

	private setError(error: string): void {
		this.state.update((state) => ({ ...state, error }));
	}

	private clearError(): void {
		this.state.update((state) => ({ ...state, error: null }));
	}

	private async clearAuthState(): Promise<void> {
		await this.clearUserFromStorage();
		// Clear the bearer token from IndexedDB
		await this.storageService.remove(StorageKeys.AuthToken);

		this.state.set({
			currentUser: null,
			isAuthenticated: false,
			isLoading: false,
			error: null,
		});
	}

	/**
	 * User storage management via StorageService
	 */
	private async saveUserToStorage(user: User): Promise<void> {
		await this.storageService.set(StorageKeys.AuthUser, user);
	}

	private async clearUserFromStorage(): Promise<void> {
		await this.storageService.remove(StorageKeys.AuthUser);
	}
}
